<template>
  <div class="flex flex-col">
    <div class="flex flex-row w-full gap-3">
      <p class="font-bold mb-1">{{ getRoleDisplayTextFromString(projectEntity.type) }}</p>
      <Button v-if="canEdit" label="Remove" severity="danger" outlined class="h-6 p-2! text-sm" @click="deleteContact"></Button>
      <EmailCompletionToggleButton v-model="projectEntity.sendCompletionEmail" @change="sendCompletionEmailChanged"></EmailCompletionToggleButton>
    </div>

    <div class="flex flex-row gap-2 pt-1 text-sm">
      <i class="pi pi-user my-auto"></i>
      <Button
        text
        :label="getEntityDisplay(projectEntity.entity)"
        severity="secondary"
        class="w-fit p-0!"
        @click="$emit('entitySelected', projectEntity.entity)"
      ></Button>
      <i v-if="projectEntity.entity.webAccountStatus" v-tooltip.top="'Web Enabled'" class="text-info-500 my-auto pi pi-globe"></i>
      <i v-if="projectEntity.entity.webAccountReadOnly" v-tooltip.top="'Read Only'" class="text-help-500 my-auto pi pi-book"></i>
      <i v-if="projectEntity.entity.active" v-tooltip.top="'Active'" class="text-success-500 my-auto pi pi-check-circle"></i>
      <i v-else="projectEntity.entity.active" v-tooltip.top="'Inactive'" class="text-danger-500 my-auto pi pi-times-circle"></i>
    </div>
    <div>
      <StackedLabelInputNumberNoForm
        v-if="projectEntity.type === ProjectRole.BUYER"
        v-model="projectEntity.balance"
        mode="currency"
        name="depositAmount"
        label=""
        classes="pi pi-money-bill me-2"
        number-class="!h-8"
        :stacked="false"
        :disabled="!canEdit"
        @update:model-value="depositUpdated"
      ></StackedLabelInputNumberNoForm>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getRoleDisplayTextFromString, ProjectRole } from "@/enums/ProjectRole";
import { getEntityDisplay } from "@/services/Utility";
import type { Entity } from "@/types/Entity";
import type { ProjectEntity } from "@/types/ProjectEntity";
import { useConfirm } from "primevue/useconfirm";
import StackedLabelInputNumberNoForm from "../non-form-inputs/StackedLabelInputNumberNoForm.vue";

const confirm = useConfirm();

const emit = defineEmits<{
  entitySelected: [entity: Entity];
  projectEntityRemoved: [ProjectEntity: ProjectEntity];
}>();

const projectEntity = defineModel<ProjectEntity>("projectEntity", { required: true });

interface Props {
  canEdit: boolean;
}
const props = defineProps<Props>();

const depositUpdated = (): void => {
  axios
    .post(`/project-entity/update-deposit/${projectEntity.value.projectEntityId}?deposit=` + projectEntity.value.balance)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          success("Success", "Buyer deposit updated");
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const deleteContact = (): void => {
  confirmPopup(
    confirm,
    "Delete Contact",
    `Are you sure you want to delete ${getRoleDisplayTextFromString(projectEntity.value.type)} ${getEntityDisplay(projectEntity.value.entity)}?`,
    () => emit("projectEntityRemoved", projectEntity.value),
    () => {}
  );
};

const sendCompletionEmailChanged = (): void => {
  nextTick(() =>
    axios
      .post(`/project-entity/${projectEntity.value.projectEntityId}/update-send-complete-email/${projectEntity.value.sendCompletionEmail}`)
      .then((response) =>
        handleApiResponse(
          response,
          (data: any) => {
            success("Success", "Approval email settings updated");
          },
          (errors: ApiError[]) => {
            error("An error occurred", errors[0].message);
            projectEntity.value.sendCompletionEmail = !projectEntity.value.sendCompletionEmail;
          }
        )
      )
      .catch((e) => errorApi(e))
  );
};
</script>
